import { useState, useEffect } from "react";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Slider } from "@/components/ui/slider";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Info, AlertTriangle } from "lucide-react";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";

interface CalculationConfig {
  source_fields: string[];
  combine_strategy: 'sum' | 'max' | 'individual';
  coverage_specs: {
    type: 'roll' | 'bundle' | 'linear' | 'area' | null;
    width_ft?: number | null;
    length_ft?: number | null;
    coverage_sqft?: number | null;
    coverage_lf?: number | null;
  };
  waste_factor: number;
  charge_method: 'unit' | 'sqft' | 'lf';
  charge_multiplier: number;
}

interface CalculationConfigEditorProps {
  value: CalculationConfig | null;
  onChange: (config: CalculationConfig) => void;
  category: string;
  unit: string;
}

const MEASUREMENT_FIELDS = [
  { value: 'area_sqft', label: 'Roof Area (sq ft)', group: 'Area' },
  { value: 'eave_lf', label: 'Eave Length (LF)', group: 'Linear' },
  { value: 'rake_lf', label: 'Rake Length (LF)', group: 'Linear' },
  { value: 'valley_lf', label: 'Valley Length (LF)', group: 'Linear' },
  { value: 'ridge_lf', label: 'Ridge Length (LF)', group: 'Linear' },
  { value: 'hip_lf', label: 'Hip Length (LF)', group: 'Linear' },
  { value: 'wall_step_lf', label: 'Sidewall Step (LF)', group: 'Linear' },
  { value: 'flashing_lf', label: 'Headwall Flashing (LF)', group: 'Linear' },
  { value: 'chimney_count', label: 'Chimney Count', group: 'Counts' },
  { value: 'skylight_count', label: 'Skylight Count', group: 'Counts' },
  { value: 'pipe_count', label: 'Pipe Count', group: 'Counts' },
  { value: 'fan_count', label: 'Fan Count', group: 'Counts' },
  { value: 'vent_count', label: 'Vent Count', group: 'Counts' },
  { value: 'layers', label: 'Layers', group: 'Other' },
];

const DEFAULT_CONFIG: CalculationConfig = {
  source_fields: [],
  combine_strategy: 'sum',
  coverage_specs: {
    type: null,
    width_ft: null,
    length_ft: null,
    coverage_sqft: null,
    coverage_lf: null,
  },
  waste_factor: 1.10,
  charge_method: 'unit',
  charge_multiplier: 1,
};

export function CalculationConfigEditor({ value, onChange, category, unit }: CalculationConfigEditorProps) {
  const [config, setConfig] = useState<CalculationConfig>(value || DEFAULT_CONFIG);

  useEffect(() => {
    if (value) {
      setConfig(value);
    }
  }, [value]);

  const updateConfig = (updates: Partial<CalculationConfig>) => {
    const newConfig = { ...config, ...updates };
    setConfig(newConfig);
    onChange(newConfig);
  };

  const updateCoverageSpecs = (updates: Partial<CalculationConfig['coverage_specs']>) => {
    const newSpecs = { ...config.coverage_specs, ...updates };
    
    // Auto-calculate coverage if width and length provided
    if (newSpecs.width_ft && newSpecs.length_ft) {
      newSpecs.coverage_sqft = Number((newSpecs.width_ft * newSpecs.length_ft).toFixed(2));
    }
    
    updateConfig({ coverage_specs: newSpecs });
  };

  const toggleSourceField = (field: string) => {
    const newFields = config.source_fields.includes(field)
      ? config.source_fields.filter(f => f !== field)
      : [...config.source_fields, field];
    updateConfig({ source_fields: newFields });
  };

  const getFormulaPreview = (): string => {
    if (config.source_fields.length === 0) return 'No measurement fields selected';
    
    const fields = config.source_fields.join(' + ');
    const waste = `× ${config.waste_factor} waste`;
    
    let calculation = '';
    if (config.coverage_specs.type === 'roll' && config.coverage_specs.coverage_sqft) {
      calculation = `÷ ${config.coverage_specs.coverage_sqft} sq ft/roll`;
    } else if (config.coverage_specs.type === 'bundle' && config.coverage_specs.coverage_sqft) {
      calculation = `÷ ${config.coverage_specs.coverage_sqft} sq ft/bundle`;
    }
    
    return `Quantity = (${fields}) ${waste} ${calculation}`.trim();
  };

  // Group measurement fields
  const fieldGroups = MEASUREMENT_FIELDS.reduce((acc, field) => {
    if (!acc[field.group]) acc[field.group] = [];
    acc[field.group].push(field);
    return acc;
  }, {} as Record<string, typeof MEASUREMENT_FIELDS>);

  return (
    <div className="space-y-6">
      <Accordion type="single" collapsible defaultValue="source-fields" className="w-full">
        <AccordionItem value="source-fields">
          <AccordionTrigger className="text-sm font-medium">
            📐 Measurement Source Fields
          </AccordionTrigger>
          <AccordionContent>
            <div className="space-y-4 pt-2">
              <p className="text-sm text-muted-foreground">
                Select which measurements to use for calculating this product's quantity
              </p>
              
              {Object.entries(fieldGroups).map(([group, fields]) => (
                <div key={group} className="space-y-2">
                  <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                    {group}
                  </p>
                  <div className="grid grid-cols-2 gap-2 pl-2">
                    {fields.map((field) => (
                      <div key={field.value} className="flex items-center space-x-2">
                        <Checkbox
                          id={field.value}
                          checked={config.source_fields.includes(field.value)}
                          onCheckedChange={() => toggleSourceField(field.value)}
                        />
                        <label
                          htmlFor={field.value}
                          className="text-sm leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-pointer"
                        >
                          {field.label}
                        </label>
                      </div>
                    ))}
                  </div>
                </div>
              ))}

              {config.source_fields.length > 1 && (
                <div className="space-y-2 pt-4 border-t">
                  <Label className="text-sm">Combine Strategy</Label>
                  <RadioGroup
                    value={config.combine_strategy}
                    onValueChange={(value: any) => updateConfig({ combine_strategy: value })}
                  >
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="sum" id="sum" />
                      <label htmlFor="sum" className="text-sm cursor-pointer">
                        ⊕ Sum all selected fields (most common)
                      </label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="max" id="max" />
                      <label htmlFor="max" className="text-sm cursor-pointer">
                        ⇧ Use maximum of selected fields
                      </label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="individual" id="individual" />
                      <label htmlFor="individual" className="text-sm cursor-pointer">
                        ➊ Use first selected field only
                      </label>
                    </div>
                  </RadioGroup>
                </div>
              )}
            </div>
          </AccordionContent>
        </AccordionItem>

        <AccordionItem value="coverage-specs">
          <AccordionTrigger className="text-sm font-medium">
            📦 Product Coverage Specifications
          </AccordionTrigger>
          <AccordionContent>
            <div className="space-y-4 pt-2">
              <div className="space-y-2">
                <Label>Product Type</Label>
                <Select
                  value={config.coverage_specs.type || 'none'}
                  onValueChange={(value) => 
                    updateCoverageSpecs({ type: value === 'none' ? null : value as any })
                  }
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select product type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">None (Manual calculation)</SelectItem>
                    <SelectItem value="roll">Roll (2D - Width × Length)</SelectItem>
                    <SelectItem value="bundle">Bundle (Counted units)</SelectItem>
                    <SelectItem value="linear">Linear (1D - Length only)</SelectItem>
                    <SelectItem value="area">Area (2D - Flat coverage)</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {config.coverage_specs.type === 'roll' && (
                <div className="space-y-3 p-4 border rounded-lg bg-muted/30">
                  <p className="text-sm font-medium">Roll Dimensions</p>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-2">
                      <Label className="text-sm">Width (ft)</Label>
                      <Input
                        type="number"
                        step="0.1"
                        placeholder="3"
                        value={config.coverage_specs.width_ft || ''}
                        onChange={(e) => updateCoverageSpecs({ 
                          width_ft: parseFloat(e.target.value) || null 
                        })}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-sm">Length (ft)</Label>
                      <Input
                        type="number"
                        step="0.1"
                        placeholder="33"
                        value={config.coverage_specs.length_ft || ''}
                        onChange={(e) => updateCoverageSpecs({ 
                          length_ft: parseFloat(e.target.value) || null 
                        })}
                      />
                    </div>
                  </div>
                  {config.coverage_specs.coverage_sqft && (
                    <p className="text-sm text-muted-foreground">
                      Total Coverage: <span className="font-semibold">{config.coverage_specs.coverage_sqft} sq ft</span> per roll
                    </p>
                  )}
                </div>
              )}

              {config.coverage_specs.type === 'bundle' && (
                <div className="space-y-3 p-4 border rounded-lg bg-muted/30">
                  <p className="text-sm font-medium">Bundle Coverage</p>
                  <div className="space-y-2">
                    <Label className="text-sm">Coverage per Bundle (sq ft)</Label>
                    <Input
                      type="number"
                      step="0.1"
                      placeholder="33.3"
                      value={config.coverage_specs.coverage_sqft || ''}
                      onChange={(e) => updateCoverageSpecs({ 
                        coverage_sqft: parseFloat(e.target.value) || null 
                      })}
                    />
                    <p className="text-xs text-muted-foreground">
                      Example: Standard shingles = 33.3 sq ft/bundle (3 bundles per square)
                    </p>
                  </div>
                </div>
              )}

              {config.coverage_specs.type === 'area' && (
                <div className="space-y-3 p-4 border rounded-lg bg-muted/30">
                  <p className="text-sm font-medium">Area Coverage</p>
                  <div className="space-y-2">
                    <Label className="text-sm">Coverage Area (sq ft)</Label>
                    <Input
                      type="number"
                      step="1"
                      placeholder="1000"
                      value={config.coverage_specs.coverage_sqft || ''}
                      onChange={(e) => updateCoverageSpecs({ 
                        coverage_sqft: parseFloat(e.target.value) || null 
                      })}
                    />
                    <p className="text-xs text-muted-foreground">
                      Example: Underlayment = 1,000 sq ft per roll
                    </p>
                  </div>
                </div>
              )}
            </div>
          </AccordionContent>
        </AccordionItem>

        <AccordionItem value="waste-factor">
          <AccordionTrigger className="text-sm font-medium">
            ♻️ Waste Factor
          </AccordionTrigger>
          <AccordionContent>
            <div className="space-y-4 pt-2">
              <div className="flex items-center justify-between">
                <Label>Waste Percentage</Label>
                <span className="text-sm font-semibold">
                  {((config.waste_factor - 1) * 100).toFixed(0)}%
                </span>
              </div>
              <Slider
                value={[config.waste_factor]}
                onValueChange={([value]) => updateConfig({ waste_factor: value })}
                min={1.0}
                max={1.25}
                step={0.01}
                className="w-full"
              />
              <div className="flex justify-between text-xs text-muted-foreground">
                <span>0% (None)</span>
                <span>10% (Typical)</span>
                <span>25% (High)</span>
              </div>
              <div className="text-xs text-muted-foreground space-y-1 p-3 bg-muted/30 rounded-lg">
                <p className="font-semibold">Common waste factors:</p>
                <ul className="list-disc list-inside space-y-0.5 pl-2">
                  <li>Shingles: 10% (1.10)</li>
                  <li>Underlayment: 5% (1.05)</li>
                  <li>Ice & Water: 10% (1.10)</li>
                  <li>Step Flashing: 15% (1.15)</li>
                </ul>
              </div>
            </div>
          </AccordionContent>
        </AccordionItem>

        <AccordionItem value="charge-method">
          <AccordionTrigger className="text-sm font-medium">
            💰 Charging Method
          </AccordionTrigger>
          <AccordionContent>
            <div className="space-y-4 pt-2">
              <p className="text-sm text-muted-foreground">
                Choose how to bill the customer for this product
              </p>
              <RadioGroup
                value={config.charge_method}
                onValueChange={(value: any) => updateConfig({ charge_method: value })}
              >
                <div className="flex items-center space-x-2 p-3 border rounded-lg">
                  <RadioGroupItem value="unit" id="charge-unit" />
                  <div className="flex-1">
                    <label htmlFor="charge-unit" className="text-sm font-medium cursor-pointer">
                      Charge by unit
                    </label>
                    <p className="text-xs text-muted-foreground">
                      Bill per {unit} (roll, bundle, piece, etc.)
                    </p>
                  </div>
                </div>
                <div className="flex items-center space-x-2 p-3 border rounded-lg">
                  <RadioGroupItem value="sqft" id="charge-sqft" />
                  <div className="flex-1">
                    <label htmlFor="charge-sqft" className="text-sm font-medium cursor-pointer">
                      Charge by square feet
                    </label>
                    <p className="text-xs text-muted-foreground">
                      Bill per sq ft of coverage (quantity auto-adjusted)
                    </p>
                  </div>
                </div>
                <div className="flex items-center space-x-2 p-3 border rounded-lg">
                  <RadioGroupItem value="lf" id="charge-lf" />
                  <div className="flex-1">
                    <label htmlFor="charge-lf" className="text-sm font-medium cursor-pointer">
                      Charge by linear feet
                    </label>
                    <p className="text-xs text-muted-foreground">
                      Bill per linear foot (quantity auto-adjusted)
                    </p>
                  </div>
                </div>
              </RadioGroup>

              <Alert className="border-amber-500/50 bg-amber-500/10">
                <AlertTriangle className="h-4 w-4 text-amber-600" />
                <AlertTitle className="text-amber-900 dark:text-amber-100">Important: Pricing Update Required</AlertTitle>
                <AlertDescription className="text-sm text-amber-800 dark:text-amber-200 space-y-2">
                  <p>
                    When you change the charging method, all prices in the <strong>Price Grid</strong> will be interpreted based on your selection:
                  </p>
                  <ul className="list-disc list-inside space-y-1 pl-2">
                    <li><strong>By unit</strong>: Prices = $ per {unit}</li>
                    <li><strong>By sq ft</strong>: Prices = $ per square foot</li>
                    <li><strong>By lf</strong>: Prices = $ per linear foot</li>
                  </ul>
                  <p className="font-semibold mt-2">
                    ⚠️ After changing this setting, update your prices in all counties to match the new unit!
                  </p>
                  <p className="text-xs italic mt-2">
                    Example: If you switch from "by roll" ($150/roll) to "by sq ft" for a 99 sq ft roll, update price to $1.52/sq ft ($150 ÷ 99 = $1.52)
                  </p>
                </AlertDescription>
              </Alert>
            </div>
          </AccordionContent>
        </AccordionItem>
      </Accordion>

      {config.source_fields.length > 0 && (
        <div className="p-4 bg-primary/5 border border-primary/20 rounded-lg space-y-2">
          <div className="flex items-start gap-2">
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger>
                  <Info className="w-4 h-4 text-primary mt-0.5" />
                </TooltipTrigger>
                <TooltipContent className="max-w-xs">
                  <p className="text-xs">
                    This formula shows how the product quantity will be calculated based on your configuration
                  </p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
            <div className="flex-1">
              <p className="text-xs font-semibold text-primary mb-1">Formula Preview:</p>
              <code className="text-xs font-mono text-foreground/80 break-all">
                {getFormulaPreview()}
              </code>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
